import {create} from 'zustand';

import {clearTokens, setAccessToken} from '@/api/clients/client';

interface User {
  id: string;
  name: string;
  isAdmin: boolean;
}

export type AuthResponse = {
  authType?: 'CI' | 'LOGIN';
  user?: {id: string; name: string; isAdmin: boolean} | null;
  ci?: string | null;
  verifiedAt?: string;
  expiresAt?: number | null;
};

interface AuthState {
  user: User | null;
  ci: string | null;
  tokenType: 'LOGIN' | 'CI' | null;
  isAuthenticated: boolean;
  ciExpiresAt: number | null;
  hydrated: boolean;

  setUser: (u: User | null) => void;
  setCI: (ci: string | null) => void;
  setFromMe: (me: AuthResponse | null) => void;
  clearAuth: () => void;

  setLoginAuth: (token: string, user: User) => void;
  setCiAuth: (token: string, user: User, ci: string, expiresAt?: number) => void;
  refreshToken: () => Promise<boolean>;
}

export const useAuthStore = create<AuthState>((set, get) => ({
  user: null,
  ci: null,
  tokenType: null,
  isAuthenticated: false,
  ciExpiresAt: null,
  hydrated: false,

  setUser: (user) =>
    set({
      user,
      isAuthenticated: !!user
    }),

  setCI: (ci) => set({ci}),

  setFromMe: (me) => {
    const isAuthenticated = !!me?.user;
    const tokenType = me?.authType || null;

    set({
      user: me?.user ?? null,
      ci: me?.ci ?? null,
      tokenType,
      isAuthenticated,
      ciExpiresAt: me?.expiresAt ?? null,
      hydrated: true
    });
  },

  clearAuth: () => {
    clearTokens();
    set({
      user: null,
      ci: null,
      tokenType: null,
      isAuthenticated: false,
      ciExpiresAt: null,
      hydrated: true
    });
  },

  setLoginAuth: (token: string, user: User) => {
    setAccessToken(token, 'LOGIN');
    set({
      user,
      tokenType: 'LOGIN',
      isAuthenticated: true,
      ci: null,
      ciExpiresAt: null
    });
  },

  setCiAuth: (token: string, user: User, ci: string, expiresAt?: number) => {
    setAccessToken(token, 'CI');
    set({
      user,
      ci,
      tokenType: 'CI',
      isAuthenticated: true,
      ciExpiresAt: expiresAt || null
    });
  },

  refreshToken: async () => {
    const {tokenType} = get();

    if (tokenType === 'CI') {
      console.log('CI tokens cannot be refreshed. Please re-authenticate.');
      return false;
    }

    try {
      console.log('refreshToken 함수 호출: /users/refresh 요청 시작');

      const response = await fetch(`${import.meta.env.VITE_BASE_API_URL}/users/refresh`, {
        method: 'POST',
        credentials: 'include'
      });

      console.log('refresh 응답 상태:', response.status);

      if (response.ok) {
        const data = await response.json();
        console.log('refresh 응답 데이터:', data);

        const {accessToken} = data.data;
        setAccessToken(accessToken, 'LOGIN');
        console.log('새 토큰 설정 완료');
        return true;
      } else {
        console.log('refresh 실패, 응답:', response.status);
        get().clearAuth();
        return false;
      }
    } catch (error) {
      console.error('Token refresh failed:', error);
      get().clearAuth();
      return false;
    }
  }
}));
